import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

interface SpouseInterviewStepProps {
  data?: any;
  profileData?: any;
  onComplete: (data: any) => void;
  onNext: () => void;
  onPrevious: () => void;
}

interface QuestionnaireData {
  futureRoleAsWife: string;
  excitementAboutPlanting: string;
  certaintyOfHusbandsCalling: string;
  confidenceInSpouseAbility: string;
  callValidators: string;
  currentMinistryInvolvement: string;
  spiritualGifts: string;
  serviceToGodDream: string;
  salvationTestimony: string;
  workFamilyBalance: string;
  parentalRelationships: string;
  addictionStruggles: string;
  guiltShameStruggles: string;
}

const initialQuestionnaireData: QuestionnaireData = {
  futureRoleAsWife: '',
  excitementAboutPlanting: '',
  certaintyOfHusbandsCalling: '',
  confidenceInSpouseAbility: '',
  callValidators: '',
  currentMinistryInvolvement: '',
  spiritualGifts: '',
  serviceToGodDream: '',
  salvationTestimony: '',
  workFamilyBalance: '',
  parentalRelationships: '',
  addictionStruggles: '',
  guiltShameStruggles: '',
};

const sections = [
  {
    title: 'Section 1: Calling & Vision',
    description: 'Help us understand your personal perspective on the call to church planting.',
    fields: [
      {
        key: 'futureRoleAsWife',
        label: 'How would you define your future role as the wife of a church planter? What areas do you plan to be a part of in the church plant?',
      },
      {
        key: 'excitementAboutPlanting',
        label: 'What excites you most about the prospect of church planting?',
      },
      {
        key: 'certaintyOfHusbandsCalling',
        label: 'Describe your level of certainty that your husband has been called to plant and lead a church.',
      },
      {
        key: 'confidenceInSpouseAbility',
        label: 'Describe your level of confidence in your spouse\'s ability to plant a church.',
      },
    ],
  },
  {
    title: 'Section 2: Ministry & Gifts',
    description: 'Share about your ministry involvement, gifts, and those who have supported your journey.',
    fields: [
      {
        key: 'callValidators',
        label: 'List the names of those who have validated yours and your spouse\'s call to plant a new church.',
      },
      {
        key: 'currentMinistryInvolvement',
        label: 'Describe how you are currently involved in ministry. What level of fulfillment do you feel in ministry?',
      },
      {
        key: 'spiritualGifts',
        label: 'What would you say are your spiritual gifts? How do you see those expressed in your ministry?',
      },
      {
        key: 'serviceToGodDream',
        label: 'If you could do anything in your service to God, what would it be?',
      },
    ],
  },
  {
    title: 'Section 3: Personal & Family',
    description: 'These questions help us understand your personal background, family dynamics, and areas where you may need support.',
    fields: [
      {
        key: 'salvationTestimony',
        label: 'At what age did you begin your relationship with Christ? Write a brief summary of how you came to Christ.',
      },
      {
        key: 'workFamilyBalance',
        label: 'Describe your work/family balance. Does your spouse agree with your assessment of your work/family balance? What boundaries have you put in place? What boundaries do you need to put in place?',
      },
      {
        key: 'parentalRelationships',
        label: 'Describe your relationship with your father. Describe your relationship with your mother.',
      },
      {
        key: 'addictionStruggles',
        label: 'Do you struggle with an addiction (pornography, alcohol, drug, gambling, nicotine, food)? If so, give a brief description of your struggle and what you\'re currently doing to address the issue.',
      },
      {
        key: 'guiltShameStruggles',
        label: 'Is there an area of your life where you continually struggle with guilt, shame or low self-esteem? How do you typically handle it?',
      },
    ],
  },
];

const SpouseInterviewStep: React.FC<SpouseInterviewStepProps> = ({
  data,
  profileData,
  onComplete,
  onNext,
  onPrevious,
}) => {
  const [questionnaire, setQuestionnaire] = useState<QuestionnaireData>(initialQuestionnaireData);
  const [currentSection, setCurrentSection] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<string>('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const autoSaveTimer = useRef<NodeJS.Timeout | null>(null);

  // Total sections: questionnaire sections + final review/submit section
  const totalSections = sections.length + 1; // +1 for the review & submit section

  useEffect(() => {
    if (data?.submitted) {
      setIsSubmitted(true);
    }
    // Load any existing questionnaire data (from draft or submitted)
    if (data?.questionnaire) {
      setQuestionnaire({ ...initialQuestionnaireData, ...data.questionnaire });
    }
  }, [data]);

  const isMarried = profileData?.maritalStatus?.toLowerCase() === 'married';

  const handleFieldChange = (field: string, value: string) => {
    const updated = { ...questionnaire, [field]: value };
    setQuestionnaire(updated);
    autoSaveDraft(updated);
  };

  const autoSaveDraft = (questionnaireData: QuestionnaireData) => {
    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
    }

    autoSaveTimer.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.post('/api/portal-candidates/onboarding/spouse-interview/draft', {
          questionnaire: questionnaireData,
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSaveStatus('Draft saved');
        setTimeout(() => setSaveStatus(''), 2000);
      } catch (err) {
        console.error('Error auto-saving spouse interview draft:', err);
      }
    }, 1000);
  };

  const handleSectionNext = async () => {
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/portal-candidates/onboarding/spouse-interview/draft', {
        questionnaire,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSaveSuccess(true);

      setTimeout(() => {
        if (currentSection < totalSections - 1) {
          setCurrentSection(currentSection + 1);
          setSaveSuccess(false);
          window.scrollTo(0, 0);
        }
      }, 600);
    } catch (err) {
      console.error('Error saving spouse interview section:', err);
      setSaveError('Failed to save your progress. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSectionPrevious = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const submitData = {
        spouseName: `${profileData?.wifeFirstName || ''} ${profileData?.wifeLastName || ''}`.trim(),
        spouseEmail: profileData?.wifeEmail || '',
        spousePhone: profileData?.wifePhone || '',
        questionnaire,
        submitted: true,
      };

      await onComplete(submitData);
      setIsSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Failed to submit. Please try again.');
      console.error('Error submitting Spouse Interview:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkipForSingle = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await onComplete({
        skipped: true,
        skippedAt: new Date().toISOString(),
      });
      onNext();
    } catch (err: any) {
      setError(err.message || 'Failed to skip this step. Please try again.');
      console.error('Error skipping Spouse Interview step:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const isSectionComplete = (sectionIndex: number): boolean => {
    if (sectionIndex >= sections.length) return false;
    const section = sections[sectionIndex];
    return section.fields.every(field => {
      const value = (questionnaire as any)[field.key];
      return value && value.trim().length > 0;
    });
  };

  const isQuestionnaireComplete = (): boolean => {
    return sections.every((_, index) => isSectionComplete(index));
  };

  const progress = ((currentSection + 1) / totalSections) * 100;

  // Not married -- skip this step
  if (!isMarried) {
    return (
      <div className="p-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-blue-800">
                This Step is for Married Candidates
              </h3>
              <p className="mt-2 text-sm text-blue-700">
                The Spouse Interview is designed for married couples as part of the assessment process.
                Since you indicated you are not married, you can skip this step.
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mt-6 flex justify-between">
          <button
            type="button"
            onClick={onPrevious}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={handleSkipForSingle}
            disabled={isLoading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Saving...' : 'Skip This Step'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Spouse Interview</h2>
        <p className="text-sm text-gray-600 mb-4">
          This questionnaire is designed to help the assessment team get to know a little bit about you
          prior to the assessment retreat. Please answer the questions with openness and honesty, so we
          can provide you with the most accurate assessment possible. At the end, you'll confirm your
          contact information so our Wife's Assessor can schedule a conversation with you.
        </p>

        <div className="p-4 bg-green-50 rounded-md border border-green-200">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">
                <strong>Auto-save enabled:</strong> Your progress will be saved automatically as you type. You can safely come back and continue later.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">
            {currentSection < sections.length
              ? `Section ${currentSection + 1} of ${totalSections}`
              : `Review & Submit`}
          </span>
          {saveStatus && (
            <span className="text-sm text-green-600">{saveStatus}</span>
          )}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-primary-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        {/* Section dots */}
        <div className="flex justify-between mt-2">
          {Array.from({ length: totalSections }).map((_, index) => (
            <button
              key={index}
              onClick={() => { setCurrentSection(index); window.scrollTo(0, 0); }}
              className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-medium transition-colors ${
                index === currentSection
                  ? 'bg-primary-600 text-white'
                  : index < sections.length && isSectionComplete(index)
                    ? 'bg-green-100 text-green-800 border border-green-300'
                    : 'bg-gray-100 text-gray-600 border border-gray-300'
              }`}
              title={index < sections.length ? sections[index].title : 'Review & Submit'}
            >
              {index < sections.length && isSectionComplete(index) ? (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              ) : (
                index + 1
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Questionnaire sections */}
      {currentSection < sections.length && !isSubmitted && (
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {sections[currentSection].title}
          </h3>
          <p className="text-sm text-gray-500 mb-6">
            {sections[currentSection].description}
          </p>

          <div className="space-y-6">
            {sections[currentSection].fields.map((field) => (
              <div key={field.key}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {field.label}
                </label>
                <textarea
                  rows={4}
                  value={(questionnaire as any)[field.key] || ''}
                  onChange={(e) => handleFieldChange(field.key, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Type your response here..."
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Review & Submit section (final section) */}
      {(currentSection === sections.length || isSubmitted) && (
        <div className="space-y-6">
          <div className="prose max-w-none">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">Schedule Your Interview</h3>
              <p className="text-sm text-blue-800 mb-4">
                As part of our assessment process, you will need to participate in a phone call or
                video call with our Wife's Assessor. This is a valuable part of the process that helps us
                understand and support your family as you pursue church planting.
              </p>
              <h4 className="text-md font-semibold text-blue-900 mb-3">What to Expect:</h4>
              <ul className="space-y-2 text-sm text-blue-800">
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Our Wife's Assessor will reach out to you to schedule a conversation</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>The call will be conducted via phone or Zoom at a convenient time</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Expect to be contacted within the next week after submitting</span>
                </li>
              </ul>
            </div>

            {isSubmitted ? (
              <div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg font-medium text-green-800">Questionnaire & Interview Request Submitted!</h3>
                      <p className="mt-2 text-sm text-green-700">
                        Your questionnaire responses and contact information have been shared with our Wife's Assessor.
                        They will be in touch within the next week to schedule the interview.
                      </p>
                      {data?.submittedAt && (
                        <p className="mt-2 text-xs text-green-600">
                          Submitted on: {new Date(data.submittedAt).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Status tracking */}
                {profileData?.spouseInterviewStatus && profileData.spouseInterviewStatus !== 'not_started' && (
                  <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Current Status</h3>
                    <div className="space-y-3">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Interview Status</dt>
                        <dd className="mt-1">
                          <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                            profileData.spouseInterviewStatus === 'complete' ? 'bg-green-100 text-green-800' :
                            profileData.spouseInterviewStatus === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                            profileData.spouseInterviewStatus === 'started' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {profileData.spouseInterviewStatus === 'not_started' && 'Not Started'}
                            {profileData.spouseInterviewStatus === 'started' && 'Awaiting Contact'}
                            {profileData.spouseInterviewStatus === 'scheduled' && 'Scheduled'}
                            {profileData.spouseInterviewStatus === 'complete' && 'Complete'}
                          </span>
                        </dd>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div>
                {/* Questionnaire completion check */}
                {!isQuestionnaireComplete() && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-yellow-800">
                          <strong>Some questions are incomplete.</strong> You can still submit, but we encourage you to answer all questions for the most helpful assessment experience.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Spouse contact info */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Your Contact Information:</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    The following contact details will be shared with our Wife's Assessor so they can schedule the interview.
                  </p>
                  <div className="grid grid-cols-1 gap-4 text-sm">
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <div>
                        <p className="font-medium text-gray-700">Name</p>
                        <p className="text-gray-600">{profileData?.wifeFirstName} {profileData?.wifeLastName}</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <div>
                        <p className="font-medium text-gray-700">Email</p>
                        <p className="text-gray-600">{profileData?.wifeEmail || 'Not provided'}</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <div>
                        <p className="font-medium text-gray-700">Phone</p>
                        <p className="text-gray-600">{profileData?.wifePhone || 'Not provided'}</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-4">
                    If this information is incorrect, please go back to your profile and update your spouse's contact details.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Save Success Message */}
      {saveSuccess && (
        <div className="mb-6 p-4 bg-green-50 rounded-md border border-green-200">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-700">Progress saved successfully!</p>
            </div>
          </div>
        </div>
      )}

      {/* Save Error Message */}
      {saveError && (
        <div className="mb-6 p-4 bg-red-50 rounded-md border border-red-200">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{saveError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation buttons */}
      <div className="flex justify-between">
        <button
          type="button"
          onClick={currentSection === 0 ? onPrevious : handleSectionPrevious}
          disabled={isLoading || isSaving}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
        >
          {currentSection === 0 ? 'Previous Step' : 'Previous Section'}
        </button>
        <div className="flex space-x-3">
          {/* During questionnaire sections: Save & Continue */}
          {currentSection < sections.length && !isSubmitted && (
            <button
              type="button"
              onClick={handleSectionNext}
              disabled={isLoading || isSaving}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  Save & Continue
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </>
              )}
            </button>
          )}

          {/* On Review & Submit section: Submit button */}
          {currentSection === sections.length && !isSubmitted && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  Submit Questionnaire & Request Interview
                </>
              )}
            </button>
          )}

          {/* After submission: Continue button */}
          {isSubmitted && (
            <button
              type="button"
              onClick={onNext}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Continue
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpouseInterviewStep;
